import { useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import { useAppDispatch, useAppSelector, type RootState } from "@app/store";
import { selectAccessToken } from "@app/store/auth.slice";
import { makeGql, GqlError } from "@app/utils/gql";
import { fetchAllPages } from "@app/utils/fetch-all-pages";
import type { Category, CategoriesPageInfo, CategoryStatus } from "@app/types/categories";
import { CATEGORY_STATUS } from "@app/types/categories";

// Re-export for convenience used by the page component
export type { CategoriesPageInfo };

// ─── Constants ────────────────────────────────────────────────────────────────

const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_ENDPOINT ?? "";
const APPSYNC_API_KEY = import.meta.env.VITE_GRAPHQL_API_KEY ?? "";
const PAGE_LIMIT = 10;

// Backend signals the last-active-category guard via error code, not HTTP status.
const LAST_ACTIVE_CATEGORY_ERROR_CODE = "LAST_ACTIVE_CATEGORY";
const LAST_ACTIVE_CATEGORY_MESSAGE = "At least one category must stay active. Re-enable another category before deactivating this one.";

const DELETE_CATEGORY_ERROR_MESSAGES: Record<string, string> = {
    FOUNDATIONAL_CATEGORY: "This is a foundational category and cannot be deleted. Deactivate it instead.",
    PROTECTED_CATEGORY: "This is a protected category and cannot be deleted.",
    LAST_ACTIVE_CATEGORY: "At least one category must stay active, so this category cannot be deleted.",
    NOT_FOUND: "This category no longer exists.",
    CONFLICT: "This category has already been deleted.",
};

function resolveDeleteCategoryError(err: unknown): string {
    if (err instanceof GqlError) {
        const mapped = DELETE_CATEGORY_ERROR_MESSAGES[err.errorType?.toUpperCase() ?? ""];
        if (mapped) return mapped;
    }
    return err instanceof Error ? err.message : "Failed to delete category";
}

function resolveCategoryMutationError(err: unknown, fallback: string): string {
    if (err instanceof GqlError && err.errorType?.toUpperCase() === LAST_ACTIVE_CATEGORY_ERROR_CODE) {
        return LAST_ACTIVE_CATEGORY_MESSAGE;
    }
    return err instanceof Error ? err.message : fallback;
}

const CATEGORIES_QUERY = `
  query Categories($limit: Int, $cursor: String, $filter: CategoriesFilter) {
    categories(limit: $limit, cursor: $cursor, filter: $filter) {
      items {
        categoryId
        categoryName
        categoryGroup
        status
        accountCode
        legacyId
        updatedAt
        updatedBy {
          userId
          userName
        }
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
    }
  }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export type CategoriesSortBy = "categoryName" | "categoryGroup" | "status" | "accountCode" | "updatedAt" | "updatedBy";

export type CategoriesSortOrder = "asc" | "desc";

export interface CategoriesColumnFilter {
    categoryName?: string;
    categoryGroup?: string;
    status?: CategoryStatus;
    accountCode?: string;
    updatedAt?: string;
    updatedByUserName?: string;
}

interface CategoriesFilter extends CategoriesColumnFilter {
    sortBy?: CategoriesSortBy;
    sortOrder?: CategoriesSortOrder;
}

interface CategoriesResponse {
    categories: {
        items: Category[];
        pageInfo: CategoriesPageInfo;
    };
}

interface UpdateCategoryInput {
    categoryName: string;
    accountCode: string;
    categoryGroup?: string;
    status?: CategoryStatus;
}

type UpdateCategoryResult = Pick<Category, "categoryId" | "categoryName" | "accountCode" | "categoryGroup" | "status">;

interface CreateCategoryInput {
    categoryName: string;
    categoryGroup: string;
    accountCode?: string;
    status?: CategoryStatus;
}

type CreateCategoryResult = Pick<Category, "categoryId" | "categoryName" | "accountCode" | "categoryGroup" | "status">;

// ─── Shared header helper ────────────────────────────────────────────────────

function useApiHeaders() {
    return useCallback((): Record<string, string> => {
        const h: Record<string, string> = {};
        if (APPSYNC_API_KEY) h["x-api-key"] = APPSYNC_API_KEY;
        return h;
    }, []);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useCategories() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    const getHeaders = useApiHeaders();

    const [categories, setCategories] = useState<Category[]>([]);
    const [pageInfo, setPageInfo] = useState<CategoriesPageInfo | null>(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [sort, setSort] = useState<{ sortBy: CategoriesSortBy; sortOrder: CategoriesSortOrder } | null>(null);
    const [columnFilter, setColumnFilter] = useState<CategoriesColumnFilter>({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Cursor history indexed by page number; index 0 is always null (first page)
    const cursorHistoryRef = useRef<Array<string | null>>([null]);
    // Track in-flight requests to prevent duplicate concurrent fetches
    const inflightRef = useRef<Set<string>>(new Set());
    // Monotonically increasing; stale completions are discarded when behind the latest
    const latestRequestIdRef = useRef(0);

    // Refs for stable reads inside callback
    const sortRef = useRef(sort);
    const columnFilterRef = useRef(columnFilter);
    sortRef.current = sort;
    columnFilterRef.current = columnFilter;

    // Avoid re-running the secondary effect on first mount
    const isFirstMount = useState(true);

    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    const loadCategories = useCallback(
        async (page: number) => {
            const cursor = cursorHistoryRef.current[page] ?? null;
            const currentSort = sortRef.current;
            const currentColumnFilter = columnFilterRef.current;
            const key = `page-${page}-cursor-${cursor ?? "null"}-sort-${JSON.stringify(currentSort)}-filter-${JSON.stringify(currentColumnFilter)}`;

            if (inflightRef.current.has(key)) return;
            inflightRef.current.add(key);

            const requestId = ++latestRequestIdRef.current;
            setIsLoading(true);
            setError(null);

            const filter: CategoriesFilter = {
                ...currentColumnFilter,
                ...(currentSort ? { sortBy: currentSort.sortBy, sortOrder: currentSort.sortOrder } : {}),
            };

            const hasFilter = Object.keys(filter).length > 0;

            try {
                const data = await gql<CategoriesResponse>(CATEGORIES_QUERY, {
                    limit: PAGE_LIMIT,
                    cursor,
                    ...(hasFilter ? { filter } : {}),
                });

                if (requestId !== latestRequestIdRef.current) return;

                const { items, pageInfo: pi } = data.categories;
                setCategories(items);
                setPageInfo(pi);

                // Store the next cursor for page+1
                if (pi.hasNextPage && pi.cursor) {
                    cursorHistoryRef.current[page + 1] = pi.cursor;
                }
            } catch (err) {
                if (requestId !== latestRequestIdRef.current) return;
                setError(err instanceof Error ? err.message : "Unexpected error loading categories");
            } finally {
                inflightRef.current.delete(key);
                if (requestId === latestRequestIdRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [gql],
    );

    // Initial load when token is available
    useEffect(() => {
        if (accessToken) {
            void loadCategories(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken]);

    // Reload when page, sort, or column filter changes (skip first mount)
    useEffect(() => {
        if (isFirstMount[0]) {
            isFirstMount[1](false);
            return;
        }
        void loadCategories(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, sort, columnFilter]);

    const setPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    const setSortExternal = useCallback((next: { sortBy: CategoriesSortBy; sortOrder: CategoriesSortOrder } | null) => {
        sortRef.current = next;
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setSort(next);
    }, []);

    const setColumnFilterExternal = useCallback((next: CategoriesColumnFilter) => {
        columnFilterRef.current = next;
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        setColumnFilter(next);
    }, []);

    const refresh = useCallback(() => {
        cursorHistoryRef.current = [null];
        setCurrentPage(0);
        void loadCategories(0);
    }, [loadCategories]);

    return {
        categories,
        pageInfo,
        currentPage,
        sort,
        isLoading,
        error,
        setPage,
        setSort: setSortExternal,
        setColumnFilter: setColumnFilterExternal,
        refresh,
    };
}

// ─── Get category by ID ───────────────────────────────────────────────────────

const GET_CATEGORY_QUERY = `
    query GetCategory($categoryId: ID!) {
        getCategory(categoryId: $categoryId) {
            categoryId
            categoryName
            accountCode
            categoryGroup
            status
            legacyId
            isFoundational
        }
    }
`;

interface GetCategoryResponse {
    getCategory: Pick<Category, "categoryId" | "categoryName" | "accountCode" | "categoryGroup" | "status" | "legacyId" | "isFoundational">;
}

export function useCategoryDetail(categoryId: string | null) {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();

    const [category, setCategory] = useState<GetCategoryResponse["getCategory"] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const inflightRef = useRef<Set<string>>(new Set());
    const currentIdRef = useRef<string | null>(null);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    useEffect(() => {
        if (!categoryId) {
            currentIdRef.current = null;
            setCategory(null);
            setIsLoading(false);
            setError(null);
            return;
        }

        currentIdRef.current = categoryId;

        const key = `category-${categoryId}`;
        if (inflightRef.current.has(key)) return;
        inflightRef.current.add(key);

        setIsLoading(true);
        setError(null);
        setCategory(null);

        gql<GetCategoryResponse>(GET_CATEGORY_QUERY, { categoryId })
            .then((data) => {
                if (currentIdRef.current === categoryId) setCategory(data.getCategory);
            })
            .catch((err) => {
                if (currentIdRef.current === categoryId) setError(err instanceof Error ? err.message : "Failed to load category");
            })
            .finally(() => {
                inflightRef.current.delete(key);
                if (currentIdRef.current === categoryId) setIsLoading(false);
            });
    }, [categoryId, gql]);

    return { category, isLoading, error };
}

const UPDATE_CATEGORY_MUTATION = `
  mutation UpdateCategory($categoryId: ID!, $input: UpdateCategoryInput!) {
    updateCategory(categoryId: $categoryId, input: $input) {
      categoryId
      categoryName
      accountCode
      categoryGroup
      status
    }
  }
`;

export function useUpdateCategory() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const updateCategory = useCallback(
        async (categoryId: string, input: UpdateCategoryInput): Promise<UpdateCategoryResult> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                const data = await gql<{ updateCategory: UpdateCategoryResult }>(UPDATE_CATEGORY_MUTATION, { categoryId, input });
                return data.updateCategory;
            } catch (err) {
                if (isMountedRef.current) {
                    setError(resolveCategoryMutationError(err, "Failed to update category"));
                }
                throw err;
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { updateCategory, isLoading, error, clearError };
}

const CREATE_CATEGORY_MUTATION = `
  mutation CreateCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      categoryId
      categoryName
      accountCode
      categoryGroup
      status
    }
  }
`;

export function useCreateCategory() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    void accessToken;

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const isMountedRef = useRef(true);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const createCategory = useCallback(
        async (input: CreateCategoryInput): Promise<CreateCategoryResult> => {
            if (isMountedRef.current) {
                setIsLoading(true);
                setError(null);
            }
            try {
                const data = await gql<{ createCategory: CreateCategoryResult }>(CREATE_CATEGORY_MUTATION, { input });
                return data.createCategory;
            } catch (err) {
                if (isMountedRef.current) {
                    setError(err instanceof Error ? err.message : "Failed to create category");
                }
                throw err;
            } finally {
                if (isMountedRef.current) {
                    setIsLoading(false);
                }
            }
        },
        [gql],
    );

    const clearError = useCallback(() => setError(null), []);

    return { createCategory, isLoading, error, clearError };
}

const DELETE_CATEGORY_MUTATION = `
  mutation DeleteCategory($categoryId: ID!) {
    deleteCategory(categoryId: $categoryId) {
      categoryId
      status
    }
  }
`;

export function useDeleteCategory() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const [isLoading, setIsLoading] = useState(false);
    const isMountedRef = useRef(true);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Throws an Error carrying a user-facing message so the caller can surface it as-is.
    const deleteCategory = useCallback(
        async (categoryId: string): Promise<void> => {
            if (isMountedRef.current) setIsLoading(true);
            try {
                await gql<{ deleteCategory: Pick<Category, "categoryId" | "status"> }>(DELETE_CATEGORY_MUTATION, { categoryId });
            } catch (err) {
                throw new Error(resolveDeleteCategoryError(err));
            } finally {
                if (isMountedRef.current) setIsLoading(false);
            }
        },
        [gql],
    );

    return { deleteCategory, isLoading };
}

// ─── All active categories (for policy rule picker) ───────────────────────────
// Policy pages category source — mirrors `categories`, gated by any policy read.

const POLICY_CATEGORIES_QUERY = `
  query PolicyCategories($limit: Int, $cursor: String, $filter: CategoriesFilter) {
    policyCategories(limit: $limit, cursor: $cursor, filter: $filter) {
      items {
        categoryId
        categoryName
        categoryGroup
        status
        accountCode
        legacyId
        updatedAt
        updatedBy {
          userId
          userName
        }
      }
      pageInfo {
        totalCount
        cursor
        limit
        hasNextPage
      }
    }
  }
`;

const POLICY_CATEGORIES_PAGE_LIMIT = 100;

type PolicyCategoryItem = Pick<Category, "categoryId" | "categoryName" | "categoryGroup" | "status" | "legacyId">;

interface ActiveCategoriesResponse {
    policyCategories: {
        items: PolicyCategoryItem[];
        pageInfo: {
            cursor: string | null;
            hasNextPage: boolean;
        };
    };
}

export function useActivePolicyCategories() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    const [categories, setCategories] = useState<PolicyCategoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!accessToken || hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        setIsLoading(true);
        setError(null);

        fetchAllPages<PolicyCategoryItem>((cursor) =>
            gql<ActiveCategoriesResponse>(POLICY_CATEGORIES_QUERY, {
                limit: POLICY_CATEGORIES_PAGE_LIMIT,
                cursor,
                filter: { status: CATEGORY_STATUS.Active },
            }).then((data) => data.policyCategories),
        )
            .then((items) => setCategories(items))
            .catch((err) => setError(err instanceof Error ? err.message : "Failed to load categories"))
            .finally(() => setIsLoading(false));
    }, [accessToken, gql]);

    return { categories, isLoading, error };
}

// ─── All categories (for policy table category name resolution) ───────────────

export function useAllPolicyCategories() {
    const dispatch = useAppDispatch();
    const store = useStore<RootState>();
    const accessToken = useAppSelector(selectAccessToken);
    const getHeaders = useApiHeaders();
    const gql = useCallback(makeGql(dispatch, store, getHeaders, GRAPHQL_ENDPOINT), [dispatch, store, getHeaders]);

    const [categoryMap, setCategoryMap] = useState<Map<string, Pick<Category, "categoryName" | "status">>>(new Map());
    const [isLoading, setIsLoading] = useState(false);
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!accessToken || hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        setIsLoading(true);

        fetchAllPages<PolicyCategoryItem>((cursor) =>
            gql<ActiveCategoriesResponse>(POLICY_CATEGORIES_QUERY, {
                limit: POLICY_CATEGORIES_PAGE_LIMIT,
                cursor,
                filter: { includeDeleted: true },
            }).then((data) => data.policyCategories),
        )
            .then((items) => {
                const map = new Map<string, Pick<Category, "categoryName" | "status">>();
                for (const cat of items) {
                    map.set(cat.categoryId, { categoryName: cat.categoryName, status: cat.status });
                }
                setCategoryMap(map);
            })
            .catch(() => setCategoryMap(new Map()))
            .finally(() => setIsLoading(false));
    }, [accessToken, gql]);

    return { categoryMap, isLoading };
}
